import { useEffect, useRef } from "react";
import { trackEvent } from "@/analytics";
import { ERROR_RESILIENCE_EVENTS } from "@/analytics/events";

/**
 * Global error handler component
 * Tracks unhandled errors and session error counts
 */
export const GlobalErrorHandler = () => {
  const sessionErrorCount = useRef(0);
  const sessionStartTime = useRef(Date.now());

  useEffect(() => {
    // Track unhandled errors
    const handleError = (event: ErrorEvent) => {
      sessionErrorCount.current += 1;

      trackEvent(ERROR_RESILIENCE_EVENTS.CRITICAL_ERROR_OCCURRED, {
        error_type: "unhandled_error",
        error_code: "UNCAUGHT_EXCEPTION",
        error_message: event.message,
        error_stack: event.error?.stack?.substring(0, 500), // Limit stack trace
        file_name: event.filename,
        line_number: event.lineno,
        column_number: event.colno,
        session_error_count: sessionErrorCount.current,
        source: "web",
      });
    };

    // Track unhandled promise rejections
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      sessionErrorCount.current += 1;

      trackEvent(ERROR_RESILIENCE_EVENTS.CRITICAL_ERROR_OCCURRED, {
        error_type: "unhandled_promise_rejection",
        error_code: "UNHANDLED_REJECTION",
        error_message: event.reason?.message || String(event.reason),
        error_stack: event.reason?.stack?.substring(0, 500),
        session_error_count: sessionErrorCount.current,
        source: "web",
      });
    };

    // Track session error count on page unload
    const handleBeforeUnload = () => {
      if (sessionErrorCount.current > 0) {
        const sessionDuration = Date.now() - sessionStartTime.current;

        trackEvent(ERROR_RESILIENCE_EVENTS.SESSION_ERROR_COUNT, {
          error_count: sessionErrorCount.current,
          session_duration_ms: sessionDuration,
          source: "web",
        });
      }
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleUnhandledRejection);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  return null; // This component doesn't render anything
};
